import React, { useEffect, useRef } from 'react';

import { FingeringSizeSlider } from '../FingeringSizeSlider';
import type { FingeringSize } from '../FingeringOverlay';

export interface FontSizePopoverProps {
  value: FingeringSize;
  onChange: (next: FingeringSize) => void;
  /** Second slider. Omitted on the scan page, which only sizes digits. */
  pitchSize?: FingeringSize;
  onPitchSizeChange?: (next: FingeringSize) => void;
  onClose: () => void;
  /** Anchor element (the toolbar button that opened this popover).
   *  Used to keep clicks on the trigger from being treated as
   *  outside-clicks (which would re-close the popover the same tick
   *  it opens). */
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  /** Which side of the trigger the panel opens toward. `bottom`
   *  (default) is the top-toolbar case; `top` opens upward for the
   *  mobile bottom bar so the panel stays on-screen. */
  placement?: 'bottom' | 'top';
}

/**
 * Small white popover anchored under the Font Size toolbar button.
 * Wraps the existing `FingeringSizeSlider` so we keep the slider's
 * accessibility / keyboard handling intact while presenting it on
 * demand instead of permanently occupying toolbar space.
 *
 * Positioned via absolute placement under a `relative` parent in
 * `ProjectToolbar`, so we avoid measuring with `getBoundingClientRect`
 * — the popover always sits flush under the right-aligned icon group.
 *
 * Closes on Escape and on outside click. Outside-click ignores the
 * trigger button so the parent's `onClick` toggle stays the source of
 * truth for open/close.
 */
export const FontSizePopover: React.FC<FontSizePopoverProps> = ({
  value,
  onChange,
  pitchSize,
  onPitchSizeChange,
  onClose,
  anchorRef,
  placement = 'bottom',
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      // Ignore clicks when THIS popover isn't actually visible. The
      // toolbar renders two copies of the action group (desktop
      // `hidden md:flex` + mobile bottom bar `md:hidden`); both mount a
      // popover when open. Without this guard the hidden copy would
      // treat a click on the visible copy's slider as an outside-click
      // and close it — so the bar vanishes the instant you grab the
      // knob. `offsetParent === null` means an ancestor is display:none.
      if (!panelRef.current || panelRef.current.offsetParent === null) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    // `mousedown` (not `click`) so we close before the trigger's
    // `click` handler runs — otherwise the button would re-open the
    // popover the same tick it closes.
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose, anchorRef]);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={
        onPitchSizeChange ? 'Fingering and pitch letter size' : 'Fingering size'
      }
      className={`
        absolute right-0 z-50 flex flex-col items-stretch gap-2
        ${
          placement === 'top'
            ? 'bottom-[calc(100%+8px)]'
            : `top-[calc(100%+8px)]`
        }
      `}
    >
      {onPitchSizeChange ? (
        <div
          className={`
            flex w-[280px] flex-col gap-2 rounded-2xl bg-white px-4 py-3
            shadow-[0_8px_40px_rgba(0,0,0,0.12)]
          `}
        >
          <div>
            <p className="mb-1 text-xs font-medium text-gray-600">Fingering</p>
            <FingeringSizeSlider bare value={value} onChange={onChange} />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-gray-600">Pitch</p>
            <FingeringSizeSlider
              bare
              glyph="A"
              value={pitchSize ?? 'small'}
              onChange={onPitchSizeChange}
              ariaLabel="Pitch letter size"
            />
          </div>
        </div>
      ) : (
        <FingeringSizeSlider value={value} onChange={onChange} />
      )}
    </div>
  );
};
